import { useEffect, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { ROUTES } from '@/lib/constants/routes';
import { UI_STRINGS } from '@/lib/constants/ui-strings';
import { API_ERROR_CODES } from '@/lib/constants/error-codes';
import { Button } from '@/components/ui/Button';
import { Logo } from '@/components/ui/Logo';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorState } from '@/components/ui/ErrorState';
import { getApiErrorCode } from '@/lib/utils/error.utils';
import { useSharedProfileView } from '@/features/shared-profile/hooks/useSharedProfileView';
import { clearShareAccessToken } from '@/features/shared-profile/api/share-access-storage';
import { SharedProfileExpiredState } from '@/features/shared-profile/components/SharedProfileExpiredState';
import { BrowsePublicPagesDialog } from '@/features/shared-profile/components/BrowsePublicPagesDialog';
import { SharedProfileContent } from '@/features/shared-profile/components/SharedProfileContent';

const TERMINAL_CODES = new Set<string>([
  API_ERROR_CODES.SHARE_NOT_FOUND,
  API_ERROR_CODES.SHARE_EXPIRED,
  API_ERROR_CODES.SHARE_REVOKED,
]);

/**
 * Read-only Shared Startup Profile view for a verified recipient (MYN-41,
 * MYN-58's "Main Content" screen). Standalone route - deliberately NOT under
 * `PublicLayout` (which renders the full marketing nav + footer): the design
 * shows a minimal chrome here (logo + "Browse Public Pages" only), matching
 * `NotFoundPage`'s "standalone, no layout wrapper" precedent in `App.tsx`.
 *
 * Renders `SharedProfileContent`: the owner's startup header and cards,
 * read-only (MYN-217, FR-180), fed by the redacted public DTO. It does not
 * mount `StartupProfileSections` itself, which carries the owner's editing,
 * export and completion behaviour.
 */
export function SharedProfileViewPage() {
  const { token = '' } = useParams<{ token: string }>();
  const S = UI_STRINGS.SHARED_PROFILE;
  const [browseOpen, setBrowseOpen] = useState(false);

  const profile = useSharedProfileView(token);
  const errorCode = getApiErrorCode(profile.error);
  const isTerminal = profile.isError && errorCode !== undefined && TERMINAL_CODES.has(errorCode);

  // A revoked/expired/not-found share is done for good - drop the stale
  // per-tab view-session token so it can't linger (it would never validate
  // again anyway, but there's no reason to keep it around).
  useEffect(() => {
    if (isTerminal) {
      clearShareAccessToken(token);
    }
  }, [isTerminal, token]);

  if (profile.isError && errorCode === API_ERROR_CODES.SHARE_NOT_VERIFIED) {
    return <Navigate to={ROUTES.SHARED_PROFILE.replace(':token', token)} replace />;
  }
  if (isTerminal) {
    return <SharedProfileExpiredState />;
  }

  return (
    <div className="min-h-screen bg-page-bg">
      <header className="flex h-20 items-center justify-between border-b border-divider bg-white px-4 sm:px-8">
        <Link to={ROUTES.HOME} aria-label={`${UI_STRINGS.BRAND_NAME} home`}>
          <Logo variant="color" height={32} aria-hidden />
        </Link>
        <Button
          type="button"
          variant="navy"
          className="inline-flex items-center gap-2 rounded-xl! px-5 py-2.5 text-sm"
          onClick={() => setBrowseOpen(true)}
        >
          {S.BROWSE_PUBLIC_PAGES}
        </Button>
      </header>

      {(profile.isLoading || profile.isError) && (
        <div className="mx-auto flex w-full max-w-4xl flex-col gap-6 px-4 py-8 sm:px-6">
          {profile.isLoading && (
            <div className="flex justify-center py-16">
              <LoadingSpinner />
            </div>
          )}
          {profile.isError && errorCode === undefined && (
            <div className="rounded-card bg-white p-8 shadow-card">
              <ErrorState title="Could not load this profile" description="Please refresh the page to try again." />
            </div>
          )}
        </div>
      )}
      {/* The profile header runs full width under the bar, as on the owner's page (FR-180). */}
      {profile.data && <SharedProfileContent profile={profile.data} />}

      <BrowsePublicPagesDialog
        open={browseOpen}
        onOpenChange={setBrowseOpen}
        onConfirm={() => {
          window.location.href = ROUTES.HOME;
        }}
      />
    </div>
  );
}
